import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "webcam-micro-ne-fonctionne-pas",
  silo: "materiel",
  h1: "Ma webcam ou mon micro ne fonctionne pas : comment régler le problème ?",
  metaTitle: "Webcam ou micro qui ne marche pas : solutions",
  metaDescription: "Votre webcam ou microphone est introuvable sur Windows ? Pilotes, autorisations, paramètres... Trouvez la solution rapidement.",
  excerpt: "En pleine visioconférence et la webcam ou le micro lâche ? Ces problèmes ont généralement des causes bien identifiées : autorisations refusées, pilotes, ou conflit d'application. Voici comment les résoudre.",
  publishedAt: "2024-02-05",
  readingMinutes: 5,
  relatedSlugs: ["imprimante-non-detectee", "pas-de-son-ordinateur", "logiciel-plante-ne-souvre-pas"],
  faq: [
    {
      question: "Ma webcam fonctionne sur Zoom mais pas sur Teams -- pourquoi ?",
      answer: "Chaque application gère ses autorisations d'accès indépendamment. Vérifiez dans les paramètres de Teams que la webcam est sélectionnée (souvent 'Caméra par défaut' dans les paramètres de périphériques). Parfois, une seule application peut 'accaparer' la webcam.",
    },
    {
      question: "Mon micro est reconnu mais le son est muet ou saturé -- comment régler ?",
      answer: "Clic droit sur l'icône son en bas à droite > Sons > onglet Enregistrement. Clic droit sur votre micro > Propriétés > Niveaux. Réglez le volume et vérifiez que le boost micro n'est pas trop élevé (ce qui cause la saturation).",
    },
    {
      question: "La webcam intégrée de mon PC portable ne fonctionne plus -- c'est une panne matérielle ?",
      answer: "Pas forcément. Les webcams intégrées peuvent être désactivées par un pilote ou une mise à jour Windows. Vérifiez dans le Gestionnaire de périphériques (Périphériques d'imagerie ou Caméras) qu'elle n'est pas désactivée.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Webcam et microphone non détectés : c'est l'une des paniques les plus courantes avant une réunion importante. Voici comment diagnostiquer et résoudre le problème en quelques minutes.",
    },
    {
      type: "h2",
      text: "Vérification 1 : les autorisations Windows",
    },
    {
      type: "p",
      text: "Windows 10 et 11 demandent votre autorisation pour que les applications accèdent à votre webcam et microphone. Ces autorisations peuvent être révoquées accidentellement. Allez dans Paramètres > Confidentialité et sécurité > Caméra (et Microphone). Vérifiez que l'accès est activé globalement ET pour l'application concernée (Teams, Zoom, Skype...).",
    },
    {
      type: "h2",
      text: "Vérification 2 : le Gestionnaire de périphériques",
    },
    {
      type: "p",
      text: "Clic droit sur Démarrer > Gestionnaire de périphériques. Cherchez votre webcam dans 'Périphériques d'imagerie' ou 'Caméras'. Si elle a une icône de flèche vers le bas, elle est désactivée -- clic droit > Activer le périphérique. Si elle a une icône de point d'exclamation jaune, le pilote est défaillant -- clic droit > Mettre à jour le pilote.",
    },
    {
      type: "h2",
      text: "Vérification 3 : les paramètres audio",
    },
    {
      type: "p",
      text: "Pour le microphone : clic droit sur l'icône son > Paramètres du son. Vérifiez dans 'Entrée' que votre micro est sélectionné comme périphérique par défaut. Parlez dans le micro -- la barre de volume doit bouger. Si elle ne bouge pas, le micro n'est pas détecté ou son niveau est à zéro.",
    },
    {
      type: "callout",
      variant: "warning",
      title: "Conflit entre applications",
      text: "Une seule application peut utiliser la webcam ou le micro à la fois sur la plupart des systèmes. Si Zoom est ouvert en arrière-plan, Teams ne pourra pas accéder à votre caméra. Fermez toutes les autres applications avant de lancer votre réunion.",
    },
    {
      type: "h2",
      text: "Réinstaller le pilote de la webcam",
    },
    {
      type: "p",
      text: "Dans le Gestionnaire de périphériques, clic droit sur votre webcam > Désinstaller le périphérique. Redémarrez le PC -- Windows réinstallera le pilote automatiquement. Si ça ne suffit pas, téléchargez le pilote depuis le site du fabricant (Logitech, Microsoft, Dell, HP...).",
    },
    {
      type: "h2",
      text: "Webcam ou micro externe non reconnu",
    },
    {
      type: "p",
      text: "Pour une webcam ou un micro USB externe : essayez un autre port USB. Testez sur un autre PC si possible pour savoir si le problème vient de l'appareil ou de votre PC. Les mêmes pilotes qui posent problème pour les [imprimantes](slug:imprimante-non-detectee) peuvent aussi affecter les périphériques USB. Un problème de [son](slug:pas-de-son-ordinateur) peut également être lié.",
    },
  ],
};

export default article;
